import React, { useState, useEffect } from 'react';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { useAuth } from '../auth/useAuth';
import './VerificationAnalyticsPage.css';

interface VerificationRecord {
  id: string;
  transactionId: string;
  userId: string;
  userFullName: string;
  city: string;
  status: 'pending' | 'completed' | 'failed' | 'identity_mismatch';
  attemptId: string;
  createdAt: string;
  completedAt?: string;
}

interface TesterStatistics {
  userId: string;
  userFullName: string;
  totalTransactions: number;
  completed: number;
  failed: number;
  pending: number;
  identityMismatch: number;
}

interface User {
  id: string;
  username: string;
  fullName: string;
  phone: string;
  city: string | string[];
  role: 'admin' | 'tester';
}

const VerificationAnalyticsPage: React.FC = () => {
  const [records, setRecords] = useState<VerificationRecord[]>([]);
  const [statistics, setStatistics] = useState<TesterStatistics[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'history' | 'statistics'>('statistics');
  const [filters, setFilters] = useState({
    userId: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50); // Количество записей на странице
  const { logout } = useAuth();
  const backend = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
      setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
    } else {
      loadStatistics();
    }
  }, [activeTab, filters]);

  // Сбрасываем страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backend}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (filters.userId !== 'all') queryParams.append('userId', filters.userId);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      
      const response = await fetch(`${backend}/admin/verification-history?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecords(data.records || []);
      } else if (response.status === 401 || response.status === 403) {
        logout();
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (filters.userId !== 'all') queryParams.append('userId', filters.userId);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      
      const response = await fetch(`${backend}/admin/tester-statistics?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics || []);
      } else if (response.status === 401 || response.status === 403) {
        logout();
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      userId: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'В процессе',
      completed: 'Успешно',
      failed: 'Ошибка',
      identity_mismatch: 'Не тот человек'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'status-pending',
      completed: 'status-completed',
      failed: 'status-failed',
      identity_mismatch: 'status-mismatch'
    };
    return classes[status] || '';
  };

  // Пагинация для истории транзакций
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = records.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Показываем все страницы если их немного
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Показываем первую страницу
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Показываем страницы вокруг текущей
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Показываем последнюю страницу
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <ProtectedRoute requireAdmin>
      <div className="verification-analytics-page">
        <div className="page-header">
          <h1>Аналитика верификаций</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button className="secondary-button" onClick={handleGoHome}>
              На главную
            </button>
            <button className="secondary-button" onClick={logout}>
              Выйти
            </button>
          </div>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="user-filter">Пользователь:</label>
            <select
              id="user-filter"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
            >
              <option value="all">Все пользователи</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.fullName} ({user.username})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="date-from">Дата начала:</label>
            <input
              id="date-from"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="date-to">Дата окончания:</label>
            <input
              id="date-to"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <button className="secondary-button" onClick={clearFilters}>
              Сбросить фильтры
            </button>
          </div>
        </div>

        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            Статистика по тестерам
          </button>
          <button 
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            История транзакций
          </button>
        </div>

        {activeTab === 'statistics' && (
          <div className="statistics-section">
            <h2>Количество транзакций по тестерам</h2>
            {statistics.length === 0 ? (
              <div className="no-data">Данные не найдены</div>
            ) : (
              <div className="statistics-table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>ФИО</th>
                      <th>Всего транзакций</th>
                      <th>Успешно</th>
                      <th>Ошибки</th>
                      <th>В процессе</th>
                      <th>Не тот человек</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.map((stat) => (
                      <tr key={stat.userId}>
                        <td>{stat.userFullName}</td>
                        <td><strong>{stat.totalTransactions}</strong></td>
                        <td className="status-completed">{stat.completed}</td>
                        <td className="status-failed">{stat.failed}</td>
                        <td className="status-pending">{stat.pending}</td>
                        <td className="status-mismatch">{stat.identityMismatch}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-section">
            <h2>История всех транзакций</h2>
            {records.length === 0 ? (
              <div className="no-data">Записи не найдены</div>
            ) : (
              <>
                <div className="records-count">
                  Найдено записей: <strong>{records.length}</strong>
                  {records.length > itemsPerPage && (
                    <span className="pagination-info">
                      {' '}(Показано {startIndex + 1}-{Math.min(endIndex, records.length)} из {records.length})
                    </span>
                  )}
                </div>
                <div className="history-table-container">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>ID транзакции</th>
                        <th>ID сессии</th>
                        <th>ФИО</th>
                        <th>Город</th>
                        <th>Дата и время</th>
                        <th>Результат</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecords.map((record) => (
                        <tr key={record.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {record.transactionId}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {record.attemptId || '-'}
                          </td>
                          <td>{record.userFullName}</td>
                          <td>{record.city}</td>
                          <td>{formatDate(record.createdAt)}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(record.status)}`}>
                              {getStatusLabel(record.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Предыдущая
                    </button>
                    
                    <div className="pagination-numbers">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            key={page}
                            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page as number)}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Следующая →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default VerificationAnalyticsPage;

